import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VehiclesService } from 'src/app/_services/vehicles.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject } from 'rxjs';
import { yearModelValidator } from 'src/app/validators/yearModelValidator';
import { ArchivesService } from 'src/app/_services/archives.service';
import { ConfirmationService, MessageService } from 'primeng/api';

@Component({
  selector: 'app-create-vehicle',
  templateUrl: './create-vehicle.component.html',
  styleUrls: ['./create-vehicle.component.scss'],
})
export class CreateVehicleComponent implements OnInit {
  loading: boolean = false;
  createVehicles$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );

  vehicleForm: FormGroup = this.formBuilder.group({
    vehicle_reference: ['', Validators.required],
    engine_no: ['', Validators.required],
    chassis_no: ['', Validators.required],
    model: ['', Validators.required],
    date_inservice: ['', Validators.required],
    service_interval: [1, Validators.required],
    year_model: ['', [Validators.required, yearModelValidator()]],
    active_inservice: [false, Validators.required],
    date_outservice: [''],
    is_archived: [false],
  });

  editFormData: any;
  fb: any;
  editFormGroups: any;
  isInEditMode: boolean | undefined;
  activeIssueForm: any;
  @Input() data: any;
  @Input() activeIssueData: any;
  @Input() technicalRecordData: any;
  /** Odometer-derived in-service date for the new truck (archive flow). */
  @Input() defaultInService: string | undefined;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public vehiclesService: VehiclesService,
    public formBuilder: FormBuilder,
    private archivesService: ArchivesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    // Simulate loading completion
    setTimeout(() => {
      this.createVehicles$.next(false);
    }, 1000);
    // console.log('data', this.archivesService.archiveForm.value);

    this.updateFormData();

    // In the archive flow, default the new truck's in-service date to its
    // first post-changeover odometer reading (still editable).
    if (this.defaultInService) {
      this.vehicleForm.patchValue({ date_inservice: this.defaultInService });
    }
  }

  // Updates the form data if in edit mode
  updateFormData() {
    this.editFormData = this.config.data?.formdata;

    if (this.editFormData) {
      if (Array.isArray(this.editFormData)) {
        if (this.editFormData.length > 0) {
          this.editFormData.forEach((desc: any, index: number) => {
            const formGroup = this.fb.group({
              vehicle_reference: desc.vehicle_reference,
              engine_no: desc.engine_no,
              chassis_no: desc.chassis_no,
              model: desc.model,
              date_inservice: desc.date_inservice,
              service_interval: desc.service_interval,
              year_model: desc.year_model,
              active_inservice: desc.active_inservice,
              date_outservice: desc.date_outservice,
              is_archived: desc.is_archived,
            });
            this.editFormGroups.push(formGroup);
            this.isInEditMode = true;
          });
        }
      } else {
        this.vehicleForm.patchValue(this.editFormData);
        this.isInEditMode = true;
      }
    } else {
      this.isInEditMode = false;
    }
  }

  get isArchiveFlow(): boolean {
    return !!this.data;
  }

  get submitButtonLabel(): string {
    // isArchiveFlow must win when both are true: the only reachable path
    // where isInEditMode is true is the archive-vehicle replacement flow
    // (embedded via archive.component.html, prefilling this form from the
    // vehicle being archived) - submitting there always archives the old
    // vehicle and creates a new one, never a plain in-place save, so the
    // button must say so.
    if (this.isArchiveFlow) {
      return 'Archive Vehicle';
    }

    return this.isInEditMode ? 'Save Vehicle' : 'Add Vehicle';
  }

  onAddVehicleFormSubmit() {
    if (this.vehicleForm.invalid) {
      this.vehicleForm.markAllAsTouched(); //validation errors
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Form is invalid',
        key: 'createVehicle',
      });
      return; // Prevent form submission if form is invalid
    }

    if (this.isArchiveFlow) {
      this.confirmArchiveVehicle();
      return;
    }

    this.submitVehicleForm();
  }

  private confirmArchiveVehicle(): void {
    this.confirmationService.confirm({
      key: 'createVehicleArchive',
      header: 'Archive Vehicle',
      message:
        'This will create the archived vehicle and move the selected records into the archive. Continue?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Archive Vehicle',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.submitVehicleForm(),
    });
  }

  private submitVehicleForm(): void {
    this.loading = true;

    // date_outservice is a non-nullable column on the backend, but a brand
    // new (still in-service) vehicle has no real out-of-service date yet -
    // sending '' for it 422s ("invalid date format"). Existing vehicles use
    // the in-service date itself as the placeholder until the vehicle is
    // actually decommissioned, so match that convention here.
    const vehiclePayload = { ...this.vehicleForm.value };
    if (!vehiclePayload.date_outservice) {
      vehiclePayload.date_outservice = vehiclePayload.date_inservice;
    }

    this.vehiclesService.addVehicle(vehiclePayload).subscribe({
      next: (res: any) => {
        const vehicleId = res.body?.id;
        this.vehiclesService.getAllVehicles().subscribe((data) => {
          this.vehiclesService.updateVehicles(data.body);
        });

        if (vehicleId && this.data) {
          this.archivesService.archiveForm.patchValue({ id: vehicleId });

          this.archivesService
            .archiveVehicle(
              this.data.id,
              this.archivesService.archiveForm.value
            )
            .subscribe({
              next: (archiveRes: any) => {
                this.vehiclesService.getAllVehicles().subscribe((data) => {
                  this.vehiclesService.updateVehicles(data.body);
                });
                this.vehiclesService.getArchivedVehicles().subscribe((data) => {
                  this.vehiclesService.updateArchivedVehicles(data.body);
                });
                const filteredIssueData = this.activeIssueData.map(
                  (item: { vehicle_reference: any; id: any }) => ({
                    vehicle_reference:
                      this.archivesService.archiveForm.value.vehicle_reference,
                    id: item.id,
                  })
                );

                this.archivesService.archiveIssue(filteredIssueData).subscribe({
                  next: (archiveIssueRes: any) => {
                    this.archivesService
                      .changeActiveIssueVehicleId({
                        vehicle_reference:
                          this.vehicleForm.value.vehicle_reference,
                        id: vehicleId,
                      })
                      .subscribe({
                        next: (changeActiveIssueVehicleIdRes: any) => {
                          this.loading = false;
                          this.messageService.add({
                            key: 'vehicles',
                            severity: 'success',
                            summary: 'Success',
                            detail:
                              'Vehicle added and issues archived successfully',
                          });
                        },
                        error: (changeActiveIssueVehicleIdError) => {
                          console.error(
                            'Error changing active issue vehicle id:',
                            changeActiveIssueVehicleIdError
                          );
                          this.loading = false;
                          this.messageService.add({
                            key: 'vehicles',
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Error changing active issue vehicle id',
                          });
                        },
                      });
                  },
                  error: (archiveIssueError) => {
                    console.error('Error archiving issue:', archiveIssueError);
                    this.loading = false;
                    this.messageService.add({
                      key: 'vehicles',
                      severity: 'error',
                      summary: 'Error',
                      detail: 'Error archiving issue',
                    });
                  },
                });

                const filteredTechnicalData = this.technicalRecordData.map(
                  (item: { vehicle_reference: any; id: any }) => ({
                    vehicle_reference:
                      this.archivesService.archiveForm.value.vehicle_reference,
                    id: item.id,
                  })
                );

                this.archivesService
                  .archiveTechnicalRecord(filteredTechnicalData)
                  .subscribe({
                    next: (archiveTechnicalRecordRes: any) => {
                      this.archivesService
                        .changeTechnicalRecordVehicleId({
                          vehicle_reference:
                            this.vehicleForm.value.vehicle_reference,
                          id: vehicleId,
                        })
                        .subscribe({
                          next: (changeTechnicalRecordVehicleIdRes: any) => {
                            this.ref.close(this.vehicleForm.value);
                            this.messageService.add({
                              key: 'vehicles',
                              severity: 'success',
                              summary: 'Success',
                              detail: 'Technical record updated successfully',
                            });
                          },
                          error: (changeTechnicalRecordVehicleIdError) => {
                            console.error(
                              'Error changing technical record vehicle id:',
                              changeTechnicalRecordVehicleIdError
                            );
                            this.loading = false;
                            this.messageService.add({
                              key: 'vehicles',
                              severity: 'error',
                              summary: 'Error',
                              detail:
                                'Error changing technical record vehicle id',
                            });
                          },
                        });
                    },
                    error: (archiveTechnicalRecordError) => {
                      console.error(
                        'Error archiving technical record:',
                        archiveTechnicalRecordError
                      );
                      this.loading = false;
                      this.messageService.add({
                        key: 'vehicles',
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Error archiving technical record',
                      });
                    },
                  });

                this.ref.close(archiveRes);
              },
              error: (archiveError) => {
                this.loading = false;
                this.messageService.add({
                  key: 'vehicles',
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Error archiving vehicle',
                });
              },
            });
        } else {
          this.loading = false;
        }

        this.loading = false;
        this.ref.close(this.vehicleForm.value);
        this.messageService.add({
          key: 'vehicles',
          severity: 'success',
          summary: 'Success',
          detail: 'Vehicle added successfully',
        });
      },
      error: (addVehicleError) => {
        console.error('Error creating vehicle:', addVehicleError);
        this.loading = false;
        this.messageService.add({
          key: 'vehicles',
          severity: 'error',
          summary: 'Error',
          detail: 'Error creating vehicle',
        });
      },
    });
  }
}
